/**
 * ConnectorCoverageGrid — TAL-1
 *
 * Sensory category visualization — dots per category.
 * Categories map to brain metaphor:
 * Semantic, Temporal, Identity, Visual, Social, Audio, Knowledge.
 */

import React from 'react';

export interface ConnectorStatus {
    name: string;
    status: string;
    source_type: string;
}

export interface ConnectorCoverage {
    categories: Record<string, ConnectorStatus[]>;
}

// Default sensory categories with expected connector counts
const SENSORY_CATEGORIES: { key: string; label: string; expected: number }[] = [
    { key: 'semantic', label: 'Semantic', expected: 4 },
    { key: 'temporal', label: 'Temporal', expected: 4 },
    { key: 'identity', label: 'Identity', expected: 1 },
    { key: 'visual', label: 'Visual', expected: 2 },
    { key: 'social', label: 'Social', expected: 5 },
    { key: 'audio', label: 'Audio', expected: 2 },
    { key: 'knowledge', label: 'Knowledge', expected: 2 },
];

interface ConnectorCoverageGridProps {
    coverage?: ConnectorCoverage;
}

export const ConnectorCoverageGrid: React.FC<ConnectorCoverageGridProps> = ({ coverage }) => {
    return (
        <div className="border border-gray-800 rounded bg-gray-900/30 p-3">
            <span className="text-[10px] font-mono uppercase tracking-wider text-gray-400 block mb-3">
                Connector Coverage
            </span>

            <div className="space-y-2">
                {SENSORY_CATEGORIES.map(({ key, label, expected }) => {
                    const connectors = coverage?.categories?.[key] || [];
                    const connected = connectors.filter(c => c.status === 'connected').length;
                    const total = Math.max(connectors.length, expected);

                    return (
                        <div key={key} className="flex items-center gap-2">
                            <span className="text-[10px] font-mono text-gray-500 w-20 truncate">
                                {label}
                            </span>
                            <div className="flex gap-0.5 flex-1">
                                {Array.from({ length: total }).map((_, i) => (
                                    <span
                                        key={i}
                                        className={`w-2 h-2 rounded-full ${
                                            i < connected
                                                ? 'bg-[#39FF14]'
                                                : 'bg-gray-700'
                                        }`}
                                        title={connectors[i]?.name || 'Not connected'}
                                    />
                                ))}
                            </div>
                            <span className="text-[9px] font-mono text-gray-600 w-10 text-right">
                                {connected}/{total}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
